import React, { useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import MarketingBackdrop from '../components/MarketingBackdrop';
import SEO from '../components/SEO';
import { submitContactMessage } from '../api/contactApi';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState(''); // 'sending' | 'success' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusType('sending');
    setStatus('Sending...');
    try {
      const { data } = await submitContactMessage(formData);
      setStatusType('success');
      setStatus(data?.detail || 'Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setStatusType('error');
      const d = err?.response?.data;
      const fieldErr = d && (d.detail || d.name?.[0] || d.email?.[0] || d.message?.[0]);
      setStatus(
        typeof fieldErr === 'string'
          ? fieldErr
          : 'Something went wrong. Please try again in a moment.'
      );
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
    <SEO
      path="/contact"
      title="Contact"
      description="Get in touch with the FinAI team — questions, feedback, partnerships, and support."
    />
    <Navbar />
    <div className="relative isolate min-h-screen pt-28 pb-20 animate-fadeIn">
      <MarketingBackdrop />
      
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)} 
        className="absolute top-24 left-4 sm:left-8 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white/50 dark:bg-black/20 hover:bg-white/80 dark:hover:bg-white/10 backdrop-blur-md rounded-xl transition-all shadow-sm z-10 group"
      >
        <ArrowLeftIcon className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" />
        Back
      </button>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-gray-900 dark:text-white">
            Contact <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-500">Support</span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 font-medium text-lg max-w-2xl mx-auto">
            Have questions or need help with your portfolio? Reach out to our team and we'll get back to you shortly.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Contact Form */}
          <div className="p-8 rounded-3xl bg-white/40 dark:bg-gray-900/40 backdrop-blur-2xl border border-white/40 dark:border-white/10 shadow-glass">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white transition-all"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Message</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-white/50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-white transition-all resize-none"
                  placeholder="How can we help you?"
                />
              </div>

              {status && (
                <div className={`p-4 rounded-xl text-sm font-semibold ${
                  statusType === 'success'
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'
                    : statusType === 'error'
                    ? 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                    : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20'
                }`}>
                  {status}
                </div>
              )}

              <button
                type="submit"
                disabled={statusType === 'sending'}
                className="w-full py-4 rounded-xl font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-[0_0_20px_rgba(99,102,241,0.5)] transform hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {statusType === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

export default Contact;
